import React, { useState, useEffect, useMemo } from 'react';
import 'leaflet/dist/leaflet.css';
import 'leaflet-side-by-side';
import { Select, Row, Col, Card, Spin, Typography, Space } from 'antd';
import { EcosystemMap } from '../map/EcosystemMap';
import Cookies from 'js-cookie';
import { fetchWMSLayers } from '../../externalAPI/huaAPI';
import { CaseStudyKey } from '../../interfaces';
import {
  ApartmentOutlined,
  EnvironmentOutlined,
  BarChartOutlined,
  SplitCellsOutlined,
  ProfileOutlined,
  GlobalOutlined,
} from '@ant-design/icons';

import ScenarioBarChart from '../elements/ScenerioBarChart';
const { Option } = Select;
const { Title, Paragraph, Text } = Typography;

declare module 'leaflet' {
  function sideBySide(left: any, right: any): any;
}

const LoadingScreen: React.FC = () => (
  <div style={{ textAlign: 'center', marginTop: '50px' }}>
    <Spin size='large' />
    <p>Loading landscape data...</p>
  </div>
);

type MapIndicatorsData = {
  label: string;
  value: string;
  description: string;
  scenarios: string[];
};

export type HuaLayer = {
  name: string;
  title: string;
};

export interface ScenarioDataItem {
  Bundles: number;
  SK?: number;
  CK?: number;
  NH?: number;
  CH?: number;
  NR?: number;
  AGP?: number;
  Color?: string;
  [key: string]: number | string | undefined;
}
export type EcosystemServicesLayer = {
  description: string;
  zoom: number;
  mapIndicators: MapIndicatorsData[];
  graphs?: Record<string, ScenarioDataItem[]>;
};

// ————————————————
// Constants
// ————————————————
const ECOSYSTEM_SERVICES_DATA: Record<CaseStudyKey, EcosystemServicesLayer[]> = {
  cyprus: [
    {
      description: `
            <p style="margin-bottom: 10px">This DSA aims to support informed decision-making for the implementation of a Master Plan for the expansion of an energy centre, taking into account the impact on biodiversity and ecosystem services in Vassilikos Bay, Cyprus. Given the local specificities of the Vassilikos area and the anticipated development, there is growing pressure on local ecosystems, affecting both biodiversity (species and habitats) and human well-being (air quality, water pollution, mental health, etc.). Hence, the ES maps illustrate how development plans may influence habitat quality, particularly in and around areas where new infrastructure is planned.</p>
            <p style="margin-bottom: 10px">The InVEST Habitat Quality Model (HQM) was used to assess current and future habitat quality. This modeling approach enables rapid evaluation of habitat conditions and changes, serving as a proxy for more detailed biodiversity assessments. The model integrates information on land use/land cover (LULC) and biodiversity threats within the study area to produce habitat quality maps. These maps aim to inform decision-makers about potential biodiversity impacts, help prioritize interventions, and support exploration of future scenarios related to the implementation of the Master Plan.</p>
            <p>The future scenarios assessed include:
                <ul style="margin-left: 20px">
                    <li><strong>Business-As-Usual Scenario (Current status):</strong> Continues current activities within the wider zone of Vassilikow bay of economic growth, resource consumption, and increased emissions, with no specific measures to avoid disrupting areas of high biodiversity values.</li>
                    <li><strong>Development Scenario:</strong> It considers the extent and intensity of the areas impacted in terms of biodiversity and ES during the implementation of Master Plan and the full operation of the energy centre, as the area surrounding the core infrastructure is severely affected.</li>
                </ul>
            </p>
            <p>
              <strong>Thematic Map:</strong> This map presents the current status of habitat quality across five classes: 1. Very Low Quality, 2. Low Quality, 3. Moderate Quality, 4. High Quality, and 5. Very High Quality.
            </p>
            <p>
            <strong>Thematic Map (Post-Development):</strong> This map illustrates the projected status of habitat quality after the implementation of the Master Plan and full operation of the energy centre. It includes five quality classes: 1. Very Low, 2. Low, 3. Moderate, 4. High, and 5. Very High.
            </p>
            `,
      zoom: 12,
      mapIndicators: [{ label: 'HQI', value: 'hqi', description: '', scenarios: ['current', 'development'] }],
    },
  ],
  greece: [
    {
      description: `
            <p style="margin-bottom: 10px">This DSA aims to support the integration of ecosystem services (ES) into planning and policy decisions related to the green energy infrastructure in Greece. Developed in the context of the winfarms’ expansion, the ES maps showcase how developmental plans influence the supply of ES, biodiversity, and human well-being, particularly in and around protected areas.</p>
            <p style="margin-bottom: 10px">By using a spatially explicit Bayesian Belief Network (BBN) modeling approach, the ES maps visualize the distribution of ES under different management scenarios. These visual outputs reveal spatial patterns, synergies, and trade-offs among ES that were deemed important to local stakeholders and actors. The output maps are presented in 5 classes (Very high, High, Medium, Low, Very low), referring to the study areas’ capacity to support and provide ES. With these maps, the DSA hopes to support decision-makers in identifying zones of conflict or synergy, prioritizing interventions, and exploring future scenarios of windfarm expansion.</p>
            <p>The future scenarios assessed include:
                <ul style="margin-left: 20px">
                    <li><strong>Development Scenario (Business-As-Usual):</strong> Focuses on expanding windfarm installations in mountainous and natural areas based on energy potential. Major landscape impacts are expected due to the need for new roads and infrastructure.</li>
                    <li><strong>Repowering Scenario (Green Economy):</strong> Prioritizes sustainability by upgrading existing wind turbines to higher-capacity models without expanding into new areas. It minimizes landscape disruption by relying on the current road network with only minor interventions.</li>
                </ul>
            </p>`,
      zoom: 10,
      mapIndicators: [
        {
          label: 'Natural Heritage',
          value: 'nh',
          description:
            'Natural environments offer opportunities for education and scientific discovery. Observing species and ecosystems helps humans gain knowledge, inspiring technological advances and fostering ecological awareness. In the case of the Greek case study areas, the data used to map “Natural heritage value” included: Habitat maps, Natura 2000 network, Land cover/use, and Species richness (Plantnet data).',
          scenarios: ['current', 'development', 'repower'],
        },
        {
          label: 'Nature-based recreation',
          value: 'nt',
          description:
            'Engagement with nature through recreational activities like hiking, swimming, and birdwatching supports physical and mental health. It also contributes to tourism and community well-being. In the case of the Greek case study areas, the data used to map “Nature-based recreation” included: Road network, Habitat maps, Natura 2000 network, Land cover/use, and location of windfarms.',
          scenarios: ['current', 'development', 'repower'],
        },
        {
          label: 'Economic Wellbeing',
          value: 'ewb',
          description:
            'Nature underpins many economic activities—such as agriculture, fisheries, forestry, tourism, and renewable energy—that generate income, create jobs, and support livelihoods. Healthy ecosystems contribute to resilient economies by sustaining resources, reducing disaster risks, and lowering long-term costs. In the case of the Greek case study areas, the data used to map “Economic Wellbeing” included: agricultural areas, Renewable energy production and Grazing potential.',
          scenarios: ['current'],
        },
        {
          label: 'Total ES Supply',
          value: 'totales',
          description:
            'The total supply of ES calculated from the average values of all ES (Natural Heritage, Nature-based recreation, Human health, Economic wellbeing).',
          scenarios: ['current', 'development', 'repower'],
        },
      ],
    },
  ],
  barcelona: [],
  pnr_narbonnaise: [
    {
      description: `
            <p style="margin-bottom: 10px">This DSA aims to support the integration of ecosystem services (ES) into planning and policy decisions related to the railway infrastructure development in the Parc naturel régional de la Narbonnaise (PNRN) in France. Developed in the context of the Ligne Nouvelle Montpellier–Perpignan rail project, the ES maps showcase how developmental plans influence the supply of ES, biodiversity, and human well-being, particularly in and around protected natural areas.</p>
            <p style="margin-bottom: 10px">By using a spatially explicit Bayesian Belief Network (BBN) modeling approach, the ESmaps visualize the distribution of ES under different management scenarios. These visual outputs reveal spatial patterns, synergies, and trade-offs among ES that were deemed important to local stakeholders and actors. The output maps are presented in 5 classes (Very high, High, Medium, Low, Very low), referring to the study areas’ capacity to support and provide ES. With these maps, the DSA hopes to support decision-makers in identifying zones of conflict or synergy, prioritizing interventions, and exploring future scenarios of railway expansion.</p>
            <p>The future scenarios assessed include:
                <ul style="margin-left: 20px">
                    <li><strong>Development Scenario (Business-As-Usual):</strong> Continues current trends of economic growth, resource consumption, and increased emissions, with no specific measures to avoid disrupting areas of high biodiversity values.</li>
                    <li><strong>Nature Conservation Scenario:</strong> Supports railway expansion while prioritizing the protection of important habitats, aiming to reduce ecological disruption in and around protected areas.</li>
                    <li><strong>Utopian Scenario (Earth Stewardship):</strong> Promotes coexistence between people and nature by rerouting infrastructure to avoid sensitive ecosystems and concentrating development within existing urban and rural areas.</li>
                </ul>
            </p>`,
      zoom: 10,
      mapIndicators: [
        {
          label: 'Biodiversity Knowledge',
          value: 'bk',
          description:
            'Natural environments offer opportunities for education and scientific discovery. Observing species and ecosystems helps humans gain knowledge, inspiring technological advances and fostering ecological awareness. In the case of the PNRN, the data used to map “Biodiversity knowledge” included: Planned railway expansions, Land cover/use, Road network, Current railways, Ramsar sites, Natura 2000 network, Species richness (Plantnet data), and Geological heritage value.',
          scenarios: ['current', 'development', 'conservation', 'utopia'],
        },
        {
          label: 'Human Health',
          value: 'hh',
          description:
            'Natural environments support both physical and mental health by offering clean air and water, nutritious food, medicinal resources, and spaces for rest, exercise, and healing. Regular interaction with nature reduces stress, promotes well-being, and can help prevent disease. In the case of the PNRN, the data used to map “Human Health” included: Nutritional value of agricultural products, water quality, habitat quality, transportation accessibility (road and railway network), and Land cover/use.',
          scenarios: ['current', 'development', 'conservation', 'utopia'],
        },
        {
          label: 'Cultural & Natural Heritage',
          value: 'nchv',
          description:
            'Landscapes, seascapes, habitats, and species often hold deep symbolic, spiritual, and cultural meaning. They contribute to a sense of identity, belonging, and continuity for individuals and communities. These natural features are tied to traditions, sacred practices, childhood memories, and shared histories—forming a vital part of both natural and cultural heritage. In the case of the PNRN, the data used to map “Natural and Cultural heritage value” included: Historical monuments and places, Geological heritage, Natura 2000 network, Ramsar sites, and a landscape naturalness index.',
          scenarios: ['current', 'development', 'conservation', 'utopia'],
        },
        {
          label: 'Economic Wellbeing',
          value: 'ewb',
          description:
            'Nature underpins many economic activities—such as agriculture, fisheries, forestry, tourism, and renewable energy—that generate income, create jobs, and support livelihoods. Healthy ecosystems contribute to resilient economies by sustaining resources, reducing disaster risks, and lowering long-term costs. In the case of the PNRN, the data used to map “Economic Wellbeing” included: agricultural production data.',
          scenarios: ['current'],
        },
        {
          label: 'Total ES Supply',
          value: 'totales',
          description:
            'The total supply of ES calculated from the average values of all ES (Biodiversity knowledge, Nature-based tourism, Natural and Cultural heritage value, Human Health, Economic Wellbeing).',
          scenarios: ['current', 'development', 'conservation', 'utopia'],
        },
      ],
      graphs: {
        current: [
          {
            Bundles: 1,
            SK: 48.065,
            CK: 26.4327,
            NH: 17.7858,
            CH: 14.6366,
            NR: 1.5453,
            AGP: 27.7467,
            Color: 'rgba(64, 0, 75, 51)',
          },
          {
            Bundles: 2,
            SK: 32.7481,
            CK: 19.0994,
            NH: 5.0483,
            CH: 9.0116,
            NR: 1.6185,
            AGP: 99.431,
            Color: 'rgba(174, 139, 189, 51)',
          },
          {
            Bundles: 3,
            SK: 57.6446,
            CK: 20.2757,
            NH: 53.1439,
            CH: 13.1122,
            NR: 5.4965,
            AGP: 82.8435,
            Color: 'rgba(247, 247, 247, 255)',
          },
          {
            Bundles: 4,
            SK: 82.2635,
            CK: 8.4547,
            NH: 43.2203,
            CH: 5.1775,
            NR: 7.005,
            AGP: 0.1019,
            Color: 'rgba(128, 197, 129, 51)',
          },
          {
            Bundles: 5,
            SK: 58.3222,
            CK: 0.1168,
            NH: 18.8421,
            CH: 0.2759,
            NR: 4.9126,
            AGP: 0.1039,
            Color: 'rgba(0, 68, 27, 51)',
          },
        ],
        development: [
          {
            Bundles: 1,
            SK: 34.2853,
            CK: 19.7744,
            NH: 2.1433,
            CH: 9.6894,
            NR: 2.4484,
            AGP: 30.2589,
            Color: 'rgba(64, 0, 75, 51)',
          },
          {
            Bundles: 2,
            SK: 36.5919,
            CK: 21.3917,
            NH: 5.4879,
            CH: 9.9461,
            NR: 2.3557,
            AGP: 98.1232,
            Color: 'rgba(174, 139, 189, 51)',
          },
          {
            Bundles: 3,
            SK: 49.1772,
            CK: 17.7944,
            NH: 45.1831,
            CH: 11.881,
            NR: 5.8881,
            AGP: 96.2856,
            Color: 'rgba(247, 247, 247, 255)',
          },
          {
            Bundles: 4,
            SK: 83.4562,
            CK: 9.0978,
            NH: 38.5948,
            CH: 5.7979,
            NR: 8.1406,
            AGP: 0.1023,
            Color: 'rgba(128, 197, 129, 51)',
          },
          {
            Bundles: 5,
            SK: 59.8943,
            CK: 0.1115,
            NH: 28.6031,
            CH: 0.247,
            NR: 5.0483,
            AGP: 0.1034,
            Color: 'rgba(0, 68, 27, 51)',
          },
        ],
        conservation: [
          {
            Bundles: 1,
            SK: 42.8572,
            CK: 25.6277,
            NH: 15.465,
            CH: 14.1355,
            NR: 2.0796,
            AGP: 24.3858,
            Color: 'rgba(64, 0, 75, 51)',
          },
          {
            Bundles: 2,
            SK: 38.6701,
            CK: 27.323,
            NH: 4.8554,
            CH: 13.5617,
            NR: 2.0686,
            AGP: 94.0729,
            Color: 'rgba(174, 139, 189, 51)',
          },
          {
            Bundles: 3,
            SK: 44.9493,
            CK: 11.9798,
            NH: 39.8749,
            CH: 7.6908,
            NR: 4.8328,
            AGP: 91.9718,
            Color: 'rgba(247, 247, 247, 255)',
          },
          {
            Bundles: 4,
            SK: 74.0203,
            CK: 5.4276,
            NH: 34.7651,
            CH: 3.4496,
            NR: 6.6934,
            AGP: 0.1027,
            Color: 'rgba(0, 68, 27, 51)',
          },
        ],
        utopia: [
          {
            Bundles: 1,
            SK: 43.7611,
            CK: 26.4443,
            NH: 16.3499,
            CH: 14.7502,
            NR: 2.128,
            AGP: 24.6385,
            Color: 'rgba(64, 0, 75, 51)',
          },
          {
            Bundles: 2,
            SK: 50.8166,
            CK: 28.6415,
            NH: 24.5081,
            CH: 14.9432,
            NR: 3.4489,
            AGP: 90.4381,
            Color: 'rgba(174, 139, 189, 51)',
          },
          {
            Bundles: 3,
            SK: 22.914,
            CK: 0.5032,
            NH: 18.3023,
            CH: 1.4617,
            NR: 3.5155,
            AGP: 97.4784,
            Color: 'rgba(247, 247, 247, 255)',
          },
          {
            Bundles: 4,
            SK: 74.3499,
            CK: 6.0936,
            NH: 34.9434,
            CH: 3.8213,
            NR: 6.688,
            AGP: 0.1025,
            Color: 'rgba(128, 197, 129, 51)',
          },
          {
            Bundles: 5,
            SK: 69.0871,
            CK: 0.0097,
            NH: 31.4944,
            CH: 0.06,
            NR: 6.5361,
            AGP: 0.1043,
            Color: 'rgba(0, 68, 27, 51)',
          },
        ],
      },
    },
  ],
};
const EcosystemServices: React.FC = () => {
  const [caseStudy, setCaseStudy] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(!caseStudy);
  const [place, setPlace] = useState<string | undefined>(undefined);
  const [indicator, setIndicator] = useState<string | undefined>(undefined);
  const [leftScenario, setLeftScenario] = useState<string | undefined>(undefined);
  const [rightScenario, setRightScenario] = useState<string | undefined>(undefined);
  const [selectedLayerConfig, setSelectedLayerConfig] = useState<EcosystemServicesLayer | undefined>(undefined);
  const [layersByCaseStudy, setLayersByCaseStudy] = useState<HuaLayer[] | undefined>(undefined);

  useEffect(() => {
    // Function to check for the cookie
    const checkForCaseStudyCookie = () => {
      const cookieValue = Cookies.get('caseStudy');

      if (cookieValue) {
        setCaseStudy(cookieValue);
        setLoading(false); // Stop loading once found
      } else {
        setTimeout(checkForCaseStudyCookie, 100);
      }
    };
    checkForCaseStudyCookie();
    return () => { };
  }, []);
  // Effect to load initial config based on Case Study
  useEffect(() => {
    setLoading(!caseStudy);
    if (caseStudy) {
      const landscapeData = ECOSYSTEM_SERVICES_DATA[caseStudy as CaseStudyKey];
      if (landscapeData && landscapeData.length > 0) {
        // Initialize with the first layer config from the selected case study
        setSelectedLayerConfig(landscapeData[0]);
        // Reset scenarios when case study changes
        setLeftScenario(undefined);
        setRightScenario(undefined);
      } else {
        setSelectedLayerConfig(undefined); // Handle case where data might be missing
      }
    }
  }, [caseStudy]);

  // Effect to load dynamic WMS layers based on Active Scenario
  useEffect(() => {
    const loadDynamicLayers = async () => {
      if (!caseStudy) return; // Don't fetch if case study isn't set

      try {
        const allLayers = await fetchWMSLayers(); // Assuming this fetches all relevant layers
        setLayersByCaseStudy(allLayers);
      } catch (error) {
        console.error('Failed to load dynamic WMS layers:', error);
        setLayersByCaseStudy(undefined);
        // Optionally: set an error state to show a message to the user
      }
    };

    loadDynamicLayers();
  }, [caseStudy]); // Add caseStudy dependency if fetching depends on it

  // Memoize indicator data lookup for performance
  const getIndicatorData = useMemo(() => {
    return (value: string): MapIndicatorsData | undefined => {
      return selectedLayerConfig?.mapIndicators.find((d) => d.value === value);
    };
  }, [selectedLayerConfig]);

  // Get scenario options from the selected indicator's scenarios array
  const scenarioOptions = useMemo(() => {
    if (!indicator) return [];

    const indicatorData = getIndicatorData(indicator);
    if (!indicatorData?.scenarios) return [];

    return indicatorData.scenarios.map((scenario) => ({
      value: scenario,
      label: scenario.charAt(0).toUpperCase() + scenario.slice(1),
    }));
  }, [indicator, getIndicatorData]);

  // Function to build the WMS layer name based on indicator and scenario
  const getLayerName = (
    indicator: string | undefined,
    scenario: string,
    place: string | undefined,
  ): HuaLayer | undefined => {
    if (!caseStudy || !indicator || !layersByCaseStudy) return undefined;
    let layerName = '';
    if (caseStudy === 'pnr_narbonnaise') {
      if (indicator === 'totales') {
        layerName = `france__total__es_${scenario}`;
      } else {
        layerName = `france__ecosystem_services__${indicator}_${scenario}`;
      }
    } else if (caseStudy === 'cyprus') {
      const defaultCyprusIndicator = selectedLayerConfig?.mapIndicators[0]?.value ?? '';

      layerName = `cyprus__habitat_quality__${indicator || defaultCyprusIndicator}_${scenario}`;
    } else {
      if (indicator === 'totales') {
        layerName = `${caseStudy}_${place}_TotalES_${scenario.charAt(0).toUpperCase() + scenario.slice(1)}`;
      } else {
        layerName = `${caseStudy}_${place}__ecosystem_services__${indicator}_${scenario}`;
      }
    }
    return layersByCaseStudy.find((layer) => layer.name === layerName);
  };

  const getBundlesLayerName = (scenario: string, place: string | undefined): HuaLayer | undefined => {
    if (!caseStudy || !layersByCaseStudy) return undefined;

    let layerName = '';

    if (caseStudy === 'pnr_narbonnaise') {
      layerName = `france__es_bundles__esb_${scenario}`;
    } else if (caseStudy === 'cyprus') {
      layerName = '';
    } else {
      layerName = `${caseStudy}_${place}__es_bundles__esb_${scenario}`;
    }
    return layersByCaseStudy.find((layer) => layer.name === layerName);
  };

  // --- Prepare Indicator Options ---
  const allIndicators = selectedLayerConfig?.mapIndicators || [];

  // --- Handlers for Indicator Selection ---
  const handleIndicatorChange = (value: string | undefined) => {
    setIndicator(value);
    setLeftScenario(undefined);
    setRightScenario(undefined);
  };

  const handlePlaceChange = (value: string | undefined) => {
    setPlace(value);
    setLeftScenario(undefined);
    setRightScenario(undefined);
  };

  // --- Handlers for scenario Selection ---
  const handleLeftScenarioChange = (value: string | undefined) => {
    setLeftScenario(value);
    // If the new value matches the right scenario, clear the right scenario
    if (value && value === rightScenario) {
      setRightScenario(undefined);
    }
  };

  const handleRightScenarioChange = (value: string | undefined) => {
    setRightScenario(value);
    // If the new value matches the left scenario, clear the left scenario
    if (value && value === leftScenario) {
      setLeftScenario(undefined);
    }
  };

  const showDualSelectors = useMemo(() => {
    // If no indicator is selected, or no scenarios available, default to single
    if (!indicator || scenarioOptions.length === 0) {
      return false;
    }
    // If caseStudy is 'cyprus', always show single selector
    if (caseStudy === 'cyprus') {
      return false;
    }
    // If there's only one scenario for the selected indicator, show single selector
    if (scenarioOptions.length === 1) {
      return false;
    }
    // Otherwise, for other case studies with multiple scenarios, show dual selectors
    return true;
  }, [indicator, caseStudy, scenarioOptions]);

  const selectedLeftLabel = scenarioOptions.find((option) => option.value === leftScenario)?.label as string;
  const selectedRightLabel = scenarioOptions.find((option) => option.value === rightScenario)?.label as string;

  // --- Render Logic ---
  if (loading || !caseStudy || !selectedLayerConfig) {
    return <LoadingScreen />;
  }

  return (
    <>
      <h1 style={{ marginBottom: '20px', color: '#329619', fontSize: '25px' }}>Home &gt; Ecosystem Services</h1>
      {/* Indicator Card */}
      <Card
        title={
          <Space align='center'>
            <ApartmentOutlined style={{ color: '#329619' }} />
            <Title level={4} style={{ color: '#329619', marginBottom: 0 }}>
              Ecosystem Options
            </Title>
          </Space>
        }
        style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)' }} // Softer shadow
        bordered={false} // Softer look without border, or true if preferred
      >
        {caseStudy === 'greece' && (
          <Row align='middle' style={{ marginBottom: 20 }} gutter={[16, 16]}>
            <Col>
              <Text strong>
                <EnvironmentOutlined style={{ marginRight: 8 }} />
                Select Place:
              </Text>
            </Col>
            <Col>
              <Select
                value={place}
                onChange={handlePlaceChange}
                placeholder='Choose a place'
                style={{ width: '100%' }}
                allowClear
                id='ecosystemSelectPlace'
              >
                <Option key='thrace' value='thrace'>
                  Thrace
                </Option>
                <Option key='karystos' value='karystos'>
                  Karystos
                </Option>
                {/* Add more places if needed */}
              </Select>
            </Col>
          </Row>
        )}

        <Row align='middle' style={{ marginBottom: 20 }} gutter={[16, 16]}>
          <Col>
            <Text strong>
              <BarChartOutlined style={{ marginRight: 8 }} />
              Select Indicator:
            </Text>
          </Col>
          <Col>
            <Select
              value={indicator}
              onChange={handleIndicatorChange}
              placeholder='Choose an indicator'
              style={{ width: '100%' }}
              allowClear
              id='ecosystemSelectLayerCentreId'
            >
              {allIndicators.map((ind) => (
                <Option key={ind.value} value={ind.value}>
                  {ind.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        {/* Only show scenario selectors if an indicator is selected */}
        {indicator && scenarioOptions.length > 0 && (
          <>
            {showDualSelectors ? (
              <>
                <Row align='middle' style={{ marginBottom: 20 }} gutter={[16, 16]}>
                  <Col>
                    <Text strong>
                      <SplitCellsOutlined style={{ marginRight: 8 }} />
                      Left Scenario:
                    </Text>
                  </Col>
                  <Col>
                    <Select
                      value={leftScenario}
                      onChange={handleLeftScenarioChange}
                      placeholder='Choose left scenario'
                      style={{ width: '100%' }}
                      allowClear
                      id='ecosystemSelectLayerLeftId'
                    >
                      {scenarioOptions
                        .filter((ind) => ind.value !== rightScenario)
                        .map((ind) => (
                          <Option key={ind.value} value={ind.value}>
                            {ind.label}
                          </Option>
                        ))}
                    </Select>
                  </Col>
                </Row>

                <Row align='middle' style={{ marginBottom: 20 }} gutter={[16, 16]}>
                  {/* No margin for the last row */}
                  <Col>
                    <Text strong>
                      <SplitCellsOutlined style={{ marginRight: 8 }} />
                      Right Scenario:
                    </Text>
                  </Col>
                  <Col>
                    <Select
                      value={rightScenario}
                      onChange={handleRightScenarioChange}
                      placeholder='Choose right scenario'
                      style={{ width: '100%' }}
                      allowClear
                      id='ecosystemSelectLayerRightId'
                    >
                      {scenarioOptions
                        .filter((ind) => ind.value !== leftScenario)
                        .map((ind) => (
                          <Option key={ind.value} value={ind.value}>
                            {ind.label}
                          </Option>
                        ))}
                    </Select>
                  </Col>
                </Row>
              </>
            ) : (
              // Single Scenario Selector (for Cyprus)
              <Row align='middle' style={{ marginBottom: 20 }} gutter={[16, 16]}>
                <Col>
                  <Text strong>
                    <SplitCellsOutlined style={{ marginRight: 8 }} />
                    Scenario:
                  </Text>
                </Col>
                <Col>
                  <Select
                    value={leftScenario}
                    onChange={handleLeftScenarioChange} // Reuse left handler for the single selector
                    placeholder='Select an Scenario'
                    style={{ width: '100%' }}
                    allowClear
                    id='ecosystemSelectLayerCentreId'
                  >
                    {scenarioOptions.map((ind) => (
                      <Option key={ind.value} value={ind.value}>
                        {ind.label}
                      </Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            )}
          </>
        )}

        {indicator && caseStudy !== 'cyprus' && (
          <Card
            size='small'
            style={{ marginTop: 16, backgroundColor: '#f9f9f9' }} // Slightly different background for nested card
          >
            <Text strong>Description:</Text>
            <Typography.Paragraph style={{ marginBottom: 0, marginTop: 4 }}>
              {getIndicatorData(indicator)?.description || 'No description available.'}
            </Typography.Paragraph>
          </Card>
        )}
      </Card>

      {/* --- Ecosystem Services Map Card --- */}
      <Card
        style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)', border: '1px solid #f0f0f0' }}
        title={
          <Space align='center'>
            <GlobalOutlined style={{ color: '#329619' }} />
            <Title level={4} style={{ color: '#329619', marginBottom: 0 }}>
              Ecosystem Services
            </Title>
          </Space>
        }
      >
        {showDualSelectors ? (
          <Row gutter={16} style={{ marginBottom: 0 }}>
            <Col span={12}>
              <div
                style={{
                  padding: '8px 0',
                  borderRadius: '4px',
                  textAlign: 'center',
                  margin: '0 auto 0 auto',
                }}
              >
                <Text strong>{selectedLeftLabel ? `${selectedLeftLabel} Scenario` : 'Left Scenario Not Selected'}</Text>
              </div>
            </Col>
            <Col span={12}>
              <div
                style={{
                  padding: '8px 0',
                  borderRadius: '4px',
                  textAlign: 'center',
                  margin: '0 auto 0 auto',
                }}
              >
                <Text strong>
                  {selectedRightLabel ? `${selectedRightLabel} Scenario` : 'Right Scenario Not Selected'}
                </Text>
              </div>
            </Col>
          </Row>
        ) : (
          <div
            style={{
              padding: '8px 0',
              borderRadius: '4px',
              textAlign: 'center',
              margin: '0 auto 0 auto',
            }}
          >
            <Text strong>{selectedLeftLabel ? `${selectedLeftLabel} Scenario` : 'Scenario Not Selected'}</Text>
          </div>
        )}
        <Row gutter={16}>
          <Col span={24}>
            <EcosystemMap
              leftLayer={leftScenario ? getLayerName(indicator, leftScenario, place) : undefined}
              rightLayer={
                showDualSelectors && rightScenario ? getLayerName(indicator, rightScenario, place) : undefined
              }
              indicator={indicator || ''}
              leftScenario={leftScenario || ''}
              rightScenario={showDualSelectors ? rightScenario || '' : ''}
              type='services'
              data={selectedLayerConfig} // Assuming your map uses this
            />
            <Typography.Paragraph style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              Learn more about Ecosystem Services:<Typography.Link href='https://guarden.org/technologies/hua/' target='_blank'> https://guarden.org/technologies/hua/</Typography.Link>
            </Typography.Paragraph>
          </Col>
        </Row>
      </Card>

      {/* --- Conditional Ecosystem Bundles Services Map Card --- */}
      {caseStudy === 'pnr_narbonnaise' && (
        <Card
          style={{ marginBottom: 24, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)', border: '1px solid #f0f0f0' }}
          title={
            <Space align='center'>
              <GlobalOutlined style={{ color: '#329619' }} />
              <Title level={4} style={{ color: '#329619', marginBottom: 0 }}>
                Ecosystem Bundles Services
              </Title>
            </Space>
          }
        >
          {showDualSelectors ? (
            <Row gutter={16}>
              {/* Adjusted margin */}
              <Col span={12}>
                <div style={{ padding: '8px 0', borderRadius: '4px', textAlign: 'center' }}>
                  <Text strong>
                    {selectedLeftLabel ? `${selectedLeftLabel} Scenario` : 'Left Scenario Not Selected'}
                  </Text>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ padding: '8px 0', borderRadius: '4px', textAlign: 'center' }}>
                  <Text strong>
                    {selectedRightLabel ? `${selectedRightLabel} Scenario` : 'Right Scenario Not Selected'}
                  </Text>
                </div>
              </Col>
            </Row>
          ) : (
            <div
              style={{
                padding: '8px 0',
                borderRadius: '4px',
                textAlign: 'center',
              }}
            >
              {/* Adjusted margin */}
              <Text strong>{selectedLeftLabel ? `${selectedLeftLabel} Scenario` : 'Scenario Not Selected'}</Text>
            </div>
          )}
          <Row gutter={16}>
            <Col span={24}>
              {/* Added margin to map container */}
              <EcosystemMap
                leftLayer={leftScenario ? getBundlesLayerName(leftScenario, place) : undefined}
                rightLayer={showDualSelectors && rightScenario ? getBundlesLayerName(rightScenario, place) : undefined}
                indicator={indicator || ''}
                leftScenario={leftScenario || ''}
                rightScenario={showDualSelectors ? rightScenario || '' : ''}
                type='bundles'
                data={selectedLayerConfig as any} // Cast if necessary, ensure your EcosystemMap handles this
              />
            </Col>
          </Row>
          {/* Bar Charts Section */}
          <Row gutter={16}>
            <Col span={showDualSelectors ? 12 : 24}>
              {selectedLayerConfig && selectedLayerConfig.graphs && (
                <ScenarioBarChart
                  data={selectedLayerConfig.graphs[leftScenario || 'Undefined'] || []}
                  title={`${selectedLeftLabel} Scenario - Bundles Overview`}
                />
              )}
            </Col>
            {showDualSelectors && (
              <Col span={12}>
                {selectedLayerConfig && selectedLayerConfig.graphs && (
                  <ScenarioBarChart
                    data={selectedLayerConfig.graphs[rightScenario || 'Undefined'] || []}
                    title={`${selectedRightLabel} Scenario - Bundles Overview`}
                  />
                )}
              </Col>
            )}
          </Row>


          {/* Add the table here, after the bar charts */}
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              marginTop: '20px',
              fontFamily: 'Arial, sans-serif',
              fontSize: '14px',
              backgroundColor: '#ffffff',
              color: '#333333'
            }}
          >
            <thead>
              <tr>
                <th style={{
                  border: '1px solid #dddddd',
                  padding: '12px',
                  textAlign: 'left',
                  backgroundColor: '#f8f8f8'
                }}>
                  NCP group¹
                </th>
                <th style={{
                  border: '1px solid #dddddd',
                  padding: '12px',
                  textAlign: 'left',
                  backgroundColor: '#f8f8f8'
                }}>
                  NCP type¹
                </th>
                <th style={{
                  border: '1px solid #dddddd',
                  padding: '12px',
                  textAlign: 'left',
                  backgroundColor: '#f8f8f8'
                }}>
                  Description of NCP¹
                </th>
                <th style={{
                  border: '1px solid #dddddd',
                  padding: '12px',
                  textAlign: 'left',
                  backgroundColor: '#f8f8f8'
                }}>
                  ES benefits
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Regulating Row */}
              <tr>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Regulating</td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Habitat creation and maintenance</td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>
                  The formation and continued production, by ecosystems and organisms within them, of ecological conditions necessary or favorable for living beings of direct or indirect importance to humans.
                </td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Habitat quality</td>
              </tr>

              {/* Material Row */}
              <tr>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Material</td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Food and feed</td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>
                  Production of food from wild, managed, or domesticated organisms, such as fish, bushmeat and edible invertebrates, beef, poultry, game, dairy products, edible crops, wild plants, mushrooms, honey.
                </td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Agricultural production (AGP)</td>
              </tr>

              {/* Non-material Parent Row (spans 3 subrows) */}
              <tr>
                <td rowSpan={3} style={{ border: '1px solid #dddddd', padding: '12px' }}>Non-material</td>

                {/* Learning and Inspiration Subrow */}
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Learning and inspiration</td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>
                  Provision, by landscapes, seascapes, habitats, or organisms, of opportunities for the development of the capabilities that allow humans to prosper through education, acquisition of knowledge, and development of skills for well-being, information, and inspiration for art and technological design. Citizen knowledge (CK) refers to the biodiversity observations recorded by citizens. Scientific knowledge (SK) refers to the assumption that the presence of protected areas suggests the presence of high biodiversity value.
                </td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Biodiversity knowledge</td>
              </tr>

              {/* Physical and Psychological Experiences Subrow */}
              <tr>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Physical and psychological experiences</td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>
                  Provision, by landscapes, seascapes, habitats, or organisms, of opportunities for physically and psychologically beneficial activities, healing, relaxation, recreation, leisure, tourism, and aesthetic enjoyment based on the close contact with nature (e.g. hiking, recreational hunting, and fishing, birdwatching, snorkeling, diving, gardening).
                </td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Nature-based recreation (NR)</td>
              </tr>

              {/* Supporting Identities Subrow */}
              <tr>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Supporting identities</td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>
                  Landscapes, seascapes, habitats, or organisms are the basis for religious, spiritual, and social-cohesion experiences, such as the provisioning of opportunities for nature for people to develop a sense of place, belonging, rootedness, or connectedness, associated with different entities of the living world (e.g. cultural, sacred and heritage landscapes, sounds, scents and sights associated with childhood experiences, iconic animals, trees or flowers).
                </td>
                <td style={{ border: '1px solid #dddddd', padding: '12px' }}>Natural heritage value (NH)<br />Cultural heritage value (CH)</td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td colSpan={4} style={{
                  border: '1px solid #dddddd',
                  padding: '8px',
                  textAlign: 'left',
                  fontStyle: 'italic',
                  fontSize: '12px'
                }}>
                  ¹as described in Diaz et al. [1].
                </td>
              </tr>
            </tfoot>
          </table>
          <p style={{
            marginTop: '20px',
            fontSize: '12px',
            lineHeight: '1.5',
            color: '#555555'
          }}>
            S. Díaz, U. Pascual, M. Stenseke, B. Martín-López, R.T. Watson, Z. Molnár, R. Hill, K.M.A. Chan, I.A. Baste, K.A. Brauman, S. Polasky, A. Church, M. Lonsdale, A. Larigauderie, P.W. Leadley, A.P.E. van Oudenhoven, F. van der Plaat, M. Schröter, S. Lavorel, Y. Aumeeruddy-Thomas, E. Bukvareva, K. Davies, S. Demissew, G. Erpul, P. Failler, C.A. Guerra, C.L. Hewitt, H. Keune, S. Lindley, Y. Shirayama, Assessing nature’s contributions to people, Science 359 (2018) 270–272. <a href="https://doi.org/10.1126/science.aap8826" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff' }}>https://doi.org/10.1126/science.aap8826</a>.
          </p>
        </Card>
      )}

      {/* --- Description Card --- */}
      {selectedLayerConfig?.description && (
        <Card
          style={{
            marginBottom: 24,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.09)',
            border: '1px solid #f0f0f0',
            marginTop: 24,
          }} // Ensure margin from cards above
          id='ecosystemDescriptionId'
          title={
            <Space align='center'>
              <ProfileOutlined style={{ color: '#329619' }} />
              <Title level={4} style={{ marginBottom: 0, color: '#329619' }}>
                Description
              </Title>
            </Space>
          }
        >
          <Row align='middle'>
            <Col span={24}>
              <Paragraph>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedLayerConfig.description,
                  }}
                />
              </Paragraph>
            </Col>
          </Row>
        </Card>
      )}
    </>
  );
};

export default EcosystemServices;
